import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Spinner } from '../components/ui';
import { LocaleSwitch } from '../components/LocaleSwitch';
import { useT } from '../i18n/locale';
import { PLATFORM_ROLE_LABEL, useAdminSession } from './session';
import AdminLogin from './AdminLogin';
import ChangePassword from './ChangePassword';
import TenantsScreen from './TenantsScreen';
import TenantDetailScreen from './TenantDetailScreen';
import AdminsScreen from './AdminsScreen';

/**
 * The platform console.
 *
 * Mounted instead of the customer app, not inside it: it has its own session
 * provider, its own cookie and its own table, and nothing here should be
 * reachable by navigating around the tenant UI.
 */
export default function AdminApp() {
  const { admin, loading, isSuper, logout } = useAdminSession();
  const location = useLocation();
  const t = useT();

  if (loading) return <Spinner label={t('common.loading')} />;
  if (!admin) return <AdminLogin />;

  // The server refuses every other admin route until the bootstrap password
  // is replaced, so there is nothing else worth rendering.
  if (admin.mustChangePassword) {
    return (
      <div className="min-h-screen bg-slate-100">
        <AdminHeader name={admin.name} role={admin.role} onLogout={logout} minimal />
        <ChangePassword forced />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100">
      <AdminHeader
        name={admin.name}
        role={admin.role}
        onLogout={logout}
        isSuper={isSuper}
        path={location.pathname}
      />
      <Routes>
        <Route path="/admin" element={<Navigate to="/admin/tenants" replace />} />
        <Route path="/admin/tenants" element={<TenantsScreen />} />
        <Route path="/admin/tenants/:id" element={<TenantDetailScreen />} />
        <Route
          path="/admin/admins"
          element={isSuper ? <AdminsScreen /> : <Navigate to="/admin/tenants" replace />}
        />
        <Route path="/admin/password" element={<ChangePassword forced={false} />} />
        <Route path="*" element={<Navigate to="/admin/tenants" replace />} />
      </Routes>
    </div>
  );
}

function AdminHeader({
  name,
  role,
  onLogout,
  isSuper = false,
  minimal = false,
  path = '',
}: {
  name: string;
  role: keyof typeof PLATFORM_ROLE_LABEL;
  onLogout: () => Promise<void>;
  isSuper?: boolean;
  minimal?: boolean;
  path?: string;
}) {
  const t = useT();
  return (
    /* Near-black rather than the app's brand colour: at a glance, this is not
       the customer application. */
    <header className="sticky top-0 z-40 bg-slate-900 text-white shadow-lg">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-4 px-4 py-2.5">
        <span className="text-base font-black">DR Inbound · Platform</span>

        {minimal ? null : (
          <nav className="flex gap-1">
            <Tab to="/admin/tenants" active={path.startsWith('/admin/tenants')}>
              {t('admin.navTenant')}
            </Tab>
            {isSuper ? (
              <Tab to="/admin/admins" active={path === '/admin/admins'}>
                {t('admin.navAdmin')}
              </Tab>
            ) : null}
            <Tab to="/admin/password" active={path === '/admin/password'}>
              {t('admin.navPassword')}
            </Tab>
          </nav>
        )}

        <div className="ml-auto flex items-center gap-3 text-sm">
          <LocaleSwitch />
          <span className="font-semibold">{name}</span>
          <span className="rounded bg-white/15 px-2 py-0.5 text-[10px] font-bold uppercase">
            {t(PLATFORM_ROLE_LABEL[role])}
          </span>
          <button
            onClick={() => void onLogout()}
            className="cursor-pointer rounded-lg border-0 bg-white/10 px-3 py-1.5 text-sm font-bold text-white hover:bg-white/20"
          >
            {t('nav.logout')}
          </button>
        </div>
      </div>
    </header>
  );
}

function Tab({ to, active, children }: { to: string; active: boolean; children: string }) {
  return (
    <Link
      to={to}
      className={`rounded-lg px-3 py-1.5 text-sm font-semibold no-underline ${
        active ? 'bg-white text-slate-900' : 'text-white/80 hover:bg-white/10'
      }`}
    >
      {children}
    </Link>
  );
}
